/**
 * One New Patient form card (R3) rendered from resolved settings: shows only
 * the fields shown in the current mode, in their configured order, with their
 * label, required mark, error and hint. The field markup itself comes from a
 * renderer map (fieldRenderers.tsx), so the card keeps NewLeadPage's existing
 * wrappers and grids.
 *
 * Layout: each renderer names the grid row it sits in. Consecutive fields that
 * share a row render inside one grid with that row's classes, so with the
 * catalog's default order the card lays out exactly as the hard-coded form
 * did; a reordered field simply starts a new grid of its row's shape.
 */
import { Fragment, type ReactNode } from 'react';
import { fieldAnchorId } from './errorList';
import { hintText } from './fieldHints';
import {
  displayLabel,
  inRenderOrder,
  isRequired,
  isShown,
  type FieldCard,
  type FormMode,
  type ResolvedField,
} from './newPatientFormRules';

export interface FieldRenderProps {
  field: ResolvedField;
  /** Scroll/focus anchor for the field's wrapper (`field-<key>`). */
  anchorId: string;
  /** Custom label, else the caller's caption, else the catalog caption. */
  label: string;
  required: boolean;
  error?: string;
  /** Hint text for the slot below the control; null when none shows. */
  hint: string | null;
  /** id of the hint element, for aria-describedby. Set only with a hint. */
  hintId?: string;
}

export interface FieldRenderer<C> {
  /** Grid row this field sits in within its card. */
  row: string;
  /** Classes of that row's grid. Absent: the field renders full width. */
  rowClassName?: string;
  /** 'header' renders beside the card title instead of in the body. */
  slot?: 'header';
  render: (props: FieldRenderProps, ctx: C) => ReactNode;
  /**
   * A second cell of the same field, placed straight after the `after` field
   * when that field follows in the same row (the name field's Last Name sits
   * after Middle Name), else straight after this field.
   */
  companion?: { after: string; render: (props: FieldRenderProps, ctx: C) => ReactNode };
  /** Full-width content placed after the row that holds this field. */
  extra?: (props: FieldRenderProps, ctx: C) => ReactNode;
}

export type FieldRendererMap<C> = Readonly<Record<string, FieldRenderer<C>>>;

/** Hint text below a control, linked to it with aria-describedby. */
export function FieldHint({ id, text }: { id: string; text: string }) {
  return (
    <p id={id} className="mt-1 text-xs text-slate-500" data-field-hint="">
      {text}
    </p>
  );
}

/**
 * Wraps a control with its hint. With no hint the control is returned as is,
 * so the markup is unchanged when hints are off.
 */
export function withHint(control: ReactNode, props: Pick<FieldRenderProps, 'hint' | 'hintId'>): ReactNode {
  if (!props.hint || !props.hintId) return control;
  return (
    <div role="group" aria-describedby={props.hintId}>
      {control}
      <FieldHint id={props.hintId} text={props.hint} />
    </div>
  );
}

export interface ConfiguredCardProps<C> {
  card: FieldCard;
  title: string;
  fields: readonly ResolvedField[];
  mode: FormMode;
  renderers: FieldRendererMap<C>;
  context: C;
  errors: Record<string, string>;
  /** Caption fallbacks by field key (role titles, Province/Postal Code). */
  captions?: Record<string, string>;
  hintsEnabled: boolean;
  /** The chosen option's hint by field key; replaces the field hint. */
  optionHints?: Record<string, string>;
  /** Shown between the header and the fields. */
  lead?: ReactNode;
  /** Shown after the fields. */
  footer?: ReactNode;
}

interface RowGroup {
  row: string;
  className?: string;
  cells: ReactNode[];
  extras: ReactNode[];
}

export function ConfiguredCard<C>({
  card,
  title,
  fields,
  mode,
  renderers,
  context,
  errors,
  captions = {},
  hintsEnabled,
  optionHints = {},
  lead,
  footer,
}: ConfiguredCardProps<C>) {
  const shown = inRenderOrder(fields).filter(
    (field) => field.card === card && Boolean(renderers[field.key]) && isShown(field, mode),
  );
  if (shown.length === 0) return null;

  const propsFor = (field: ResolvedField): FieldRenderProps => {
    const hint = hintText(field, hintsEnabled, optionHints[field.key]);
    return {
      field,
      anchorId: fieldAnchorId(field.key),
      label: displayLabel(field, captions),
      required: isRequired(field, mode),
      error: errors[field.key] || undefined,
      hint,
      hintId: hint ? `field-hint-${field.key}` : undefined,
    };
  };

  const headerFields = shown.filter((field) => renderers[field.key].slot === 'header');
  const bodyFields = shown.filter((field) => renderers[field.key].slot !== 'header');

  const groups: RowGroup[] = [];
  let deferred: { after: string; node: ReactNode } | null = null;
  bodyFields.forEach((field, index) => {
    const renderer = renderers[field.key];
    const props = propsFor(field);
    let group = groups.length > 0 ? groups[groups.length - 1] : undefined;
    if (!group || group.row !== renderer.row) {
      group = { row: renderer.row, className: renderer.rowClassName, cells: [], extras: [] };
      groups.push(group);
    }
    group.cells.push(<Fragment key={field.key}>{renderer.render(props, context)}</Fragment>);

    if (deferred && deferred.after === field.key) {
      group.cells.push(deferred.node);
      deferred = null;
    }

    if (renderer.companion) {
      const node = <Fragment key={`${field.key}-companion`}>{renderer.companion.render(props, context)}</Fragment>;
      const next = bodyFields[index + 1];
      if (next && next.key === renderer.companion.after && renderers[next.key].row === renderer.row) {
        deferred = { after: next.key, node };
      } else {
        group.cells.push(node);
      }
    }

    if (renderer.extra) {
      group.extras.push(<Fragment key={`${field.key}-extra`}>{renderer.extra(props, context)}</Fragment>);
    }
  });

  const headerProps = headerFields.map(propsFor);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
      {headerFields.length > 0 ? (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {headerFields.map((field, i) => (
            <Fragment key={field.key}>{renderers[field.key].render(headerProps[i], context)}</Fragment>
          ))}
        </div>
      ) : (
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      )}

      {headerProps.map((props) => (
        <Fragment key={`${props.field.key}-notes`}>
          {props.hint && props.hintId && <FieldHint id={props.hintId} text={props.hint} />}
          {props.error && (
            <p role="alert" className="text-xs text-red-600">
              {props.error}
            </p>
          )}
        </Fragment>
      ))}

      {lead}

      {groups.map((group, i) => (
        <Fragment key={`${group.row}-${i}`}>
          {group.className ? <div className={group.className}>{group.cells}</div> : group.cells}
          {group.extras}
        </Fragment>
      ))}

      {footer}
    </div>
  );
}

